import React, { useEffect } from 'react';
import './App.css';

const expertise = [
  {
    title: 'AI Product & Strategy',
    description: 'Translating business problems into practical AI solutions, first determining whether AI is the right solution, then identifying which AI approach best fits the problem. Experience spans use-case discovery, stakeholder management, prioritisation, reliability requirements, evaluation on representative data, model and architecture selection, and decisions around external APIs versus privately hosted AI based on capability, cost, security, and data sensitivity.',
    tags: ['AI Product', 'AI Strategy', 'Use-case Discovery', 'Solution Design', 'Enterprise AI'],
    wide: true
  },
  {
    title: 'LLMOps',
    description: 'Designing, deploying, and operating private LLM infrastructure for organisational workloads, including use cases involving sensitive data. Experience spans model serving, vLLM, inference optimisation, resource efficiency, monitoring, failover, reliability, and cost optimisation.',
    tags: ['Private AI', 'vLLM', 'Inference Optimisation', 'Reliability', 'LLMOps']
  },
  {
    title: 'AI Agents & Applied AI',
    description: 'Designing and building AI agents and automation for real-world workflows, with a focus on reliability, evaluation, and taking systems from proof of concept toward production. Experience spans agentic workflows, tool use, RAG, document ingestion, and operational automation.',
    tags: ['AI Agents', 'RAG', 'Automation', 'Tool Use', 'Document Ingestion']
  },
  {
    title: 'AI Training & Evaluation',
    description: 'Evaluating and adapting models based on real task requirements rather than relying solely on general-purpose benchmarks. Experience includes fine-tuning (ML, DL, LLM), task-specific benchmarking, LLM-as-a-judge evaluation, model selection, embedding-model fine-tuning, and assessing trade-offs across capability, reliability, and resource constraints.',
    tags: ['LLM Evaluation', 'Benchmarking', 'Fine-tuning', 'Embeddings', 'LLM-as-a-Judge']
  },
  {
    title: 'AI Safety, Privacy & Security',
    description: 'Researching and evaluating the security, privacy, and reliability risks of modern AI systems, while also applying AI to cybersecurity use cases. Experience includes LLM red teaming, prompt injection and jailbreak testing, adversarial AI, data poisoning, PII guardrails, output validation, privacy-focused AI research, and model evaluation and fine-tuning for cybersecurity workflows.',
    tags: ['AI Safety', 'Privacy', 'AI Security', 'AI for Cybersecurity', 'Red Teaming', 'Adversarial AI']
  },
];

const experiences = [
  {
    organisation: 'PayNet (Payments Network Malaysia)',
    role: 'AI Research Engineer',
    date: 'January 2025 - Present',
    logo: '/paynet-logo.png',
    logoClass: 'logo-paynet',
    summary: 'Working across AI engineering, research, and product to identify valuable AI use cases, evaluate the right technical approaches, and take AI systems from experimentation toward reliable production use.',
    bullets: [
      'Took an existing AI proof of concept with significant false-positive challenges and developed it into a more reliable agentic investigation and classification system, while taking on product responsibilities to move the solution toward production.',
      'Worked directly with internal stakeholders to understand workflows, identify where AI could create value, prioritise use cases, define reliability requirements, evaluate candidate models on representative data, and recommend appropriate technical approaches.',
      'Built and optimised private LLM infrastructure supporting organisational workloads across engineering, cybersecurity, operational, and non-technical use cases, including workflows involving sensitive data.',
      'Designed task-specific evaluations and benchmarks to guide model and system decisions across capability, reliability, security, and resource constraints.',
      'Worked across LLM fine-tuning, RAG, AI safety, red teaming, privacy guardrails, inference optimisation, and applied AI strategy.'
    ]
  },
  {
    organisation: 'Imperial College London',
    role: 'Visiting Researcher - AI Security and Privacy Lab',
    date: 'January 2026 - March 2026',
    logo: '/imperial-logo.png',
    logoClass: 'logo-imperial',
    summary: 'Conducted research in AI safety, security, and privacy with the AI Security and Privacy Lab at Imperial College London.',
    bullets: [
      'Worked on trustworthy AI and privacy research, including extending text-anonymisation evaluation to the Malaysian context and studying re-identification risk beyond direct identifiers.',
      'Worked across adversarial AI, privacy, model and system evaluation, and the security limitations of modern AI systems.',
      <>Research from the work was accepted at <a href="https://openreview.net/pdf?id=q9WR23mzHP" target="_blank" rel="noreferrer">two ICML workshops</a>.</>
    ]
  }
];

const projects = [
  {
    title: 'YK Co-Finder 2.0',
    description: <>The latest version of YK Co-Finder, built to help existing and future Yayasan Khazanah Scholars <strong>identify avenues where they can contribute their skills and talents</strong> during their bond-serving period. Version 1 covered more than 20 companies; this version contains more than 1,000 public and private companies.</>,
    tags: ['AI', 'Automation'],
    links: [{ label: 'View Project', href: 'https://cofinder.davidchew.my' }]
  },
  {
    title: 'YK Co-Finder 1.0',
    description: 'An open-source, community-driven platform I built after experiencing how difficult and time-consuming it was to identify potential bond-serving companies as a Yayasan Khazanah scholar. The system uses AI to extract and classify shareholder information from company financial reports, combined with human-in-the-loop verification to improve accuracy and build a reusable database for the community.',
    tags: ['LLM', 'Data Extraction', 'Human-in-the-loop', 'Open Source'],
    links: [{ label: 'Read Launch Post', href: 'https://www.linkedin.com/posts/david-chew-hl_as-a-yayasan-khazanah-scholar-and-recent-activity-7286707246762274817-hk3R' }]
  },
  {
    title: 'Analyzing 99 Speedmart Store Locations',
    content: (
      <div className="project-copy">
        <p>In Malaysia, 99 Speedmart stores are a common sight. This mini-market chain has been mushrooming in communities to make shopping more convenient and accessible. This <strong>inspired me to start this passion project, exploring the data behind their store locations</strong>. Based on the analysis, If I were to choose a new location for a 99 Speedmart store, I'd pick a spot about <strong>1.99 kilometers</strong> away from another store, within walking distance of a residential area.</p>
        <p>Here's a breakdown of my approach:</p>
        <ol>
          <li><strong>Data Collection:</strong> <strong>web scraped location details</strong> from 99 Speedmart's website to obtain postcodes and <strong>GPS coordinates</strong> of their stores.</li>
          <li><strong>Data Cleaning &amp; Grouping:</strong> <strong>ensured data is properly formatted</strong>, corrected errors from the source, and grouped stores based on the first two digits of their postcodes.</li>
          <li><strong>Distance Calculation:</strong> <strong>calculated the distances between coordinates</strong> using GeoPy to find the shortest, longest among the shortest, and average of the shortest distances between stores in each area.</li>
          <li><strong>Visualisation:</strong> plotted these distances on maps to <strong>visualize their distribution</strong>.</li>
          <li><strong>Interactive Demo:</strong> <strong>developed a demo on Hugging Face Spaces</strong>, allowing anyone to explore and analyze the data.</li>
        </ol>
        <p>You may <strong>read the full post here</strong> and try out the <strong>Gradio demo.</strong></p>
      </div>
    ),
    tags: ['Data Analysis', 'Web Scraping', 'Data Cleaning', 'Data Visualisation', 'Gradio'],
    links: [
      { label: 'Read Post', href: 'https://www.linkedin.com/posts/david-chew-hl_analyzing-99-speedmart-store-locations-activity-7199736240227704833-NLKL' },
      { label: 'View Demo', href: 'https://huggingface.co/spaces/David-Chew-HL/99-Speedmart' }
    ]
  },
  {
    title: 'Stock Market Prediction with Deep Learning and Sentiment Analysis',
    content: (
      <div className="project-copy">
        <p><strong>Researched and developed</strong> a deep learning model that <strong>predicts stock trends 5 days ahead</strong>, using both technical and sentiment data. The model is integrated into a fully automated <strong>MLOps pipeline</strong>, handling daily data collection, feature engineering, and inference, with <strong>automatic monthly retraining</strong>.</p>
        <p>Developed a <strong>web application</strong> to showcase the model's performance, displaying predictions and allowing users to <strong>create their own stock portfolios</strong> which will be automatically managed based on the model's forecasts.</p>
        <p>The platform is hosted on Vercel and is <strong>publicly available.</strong></p>
      </div>
    ),
    tags: ['Commercial Potential', 'Deep Learning', 'Forecasting', 'ML Ops', 'Sentiment Analysis', 'NLP', 'LLM', 'React', 'Tailwind CSS', 'GitHub Actions', 'Vercel'],
    links: [{ label: 'View Project', href: 'https://fyp-interface.vercel.app/' }]
  },
  {
    title: 'Utilities Calculator for Rental Units - iCalc',
    content: (
      <div className="project-copy">
        <p><strong>Calculating and dividing utility costs</strong> among housemates can be <strong>challenging</strong>, especially with varying room configurations and air conditioning usage. <strong>Faced with this issue at my rental unit, I designed a solution to simplify and automate the process.</strong></p>
        <p><strong>iCalc offers:</strong></p>
        <ul>
          <li>Customizable Configurations: <strong>Save specific rental unit setups</strong>, including the number of tenants, rooms, and air conditioning presence.</li>
          <li>Automated Calculations: <strong>Automatically compute each tenant’s share</strong> of utility costs based on room configurations and usage.</li>
        </ul>
        <p>This tool has significantly streamlined utility management in our rental unit, <strong>reducing calculation time to less than 60 seconds</strong>.</p>
      </div>
    ),
    tags: ['Commercial Potential', 'PHP', 'JavaScript', 'MySQL', 'Automation', 'Web Hosting'],
    links: [
      { label: 'Watch Demo', href: 'https://youtu.be/qsSBtmbXd98' },
      { label: 'View Project', href: 'http://icalc.000.pe' }
    ]
  }
];

function Tag({ children }) {
  return <span className="tag">{children}</span>;
}

function App() {
  useEffect(() => {
    document.title = 'David Chew Hong Liang';
  }, []);

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="David Chew Hong Liang home">
          <img src="/dc-logo.jpg" alt="DC logo" />
        </a>
        <nav aria-label="Primary navigation">
          <a href="#expertise">Expertise</a>
          <a href="#experience">Experience</a>
          <a href="#projects">Passion Projects</a>
          <a className="linkedin-link" href="https://www.linkedin.com/in/david-chew-hl/" target="_blank" rel="noreferrer">LinkedIn</a>
        </nav>
      </header>

      <main id="top" className="container">
        <section className="hero" aria-labelledby="intro-heading">
          <h1 id="intro-heading">David Chew Hong Liang</h1>
          <p className="hero-lead">Hi, I'm David, an AI Research Engineer working across AI strategy, LLMOps, AI agents, model evaluation, AI safety, privacy, and cybersecurity.</p>
          <p>I work across the AI lifecycle - from identifying valuable use cases and evaluating models to fine-tuning, building AI agents, deploying private LLMs, and improving their performance, reliability, and cost. I work closely with stakeholders to turn real organisational problems into AI systems that are practical, secure, scalable, and trustworthy.</p>
          <p>I'm particularly interested in the intersection of AI research, engineering, and strategy: understanding what models can do, where they fail, and how to deploy them responsibly in real-world workflows.</p>
          <p>Beyond my technical work, I care deeply about giving back to society and using technology to create positive impact in the community.</p>
        </section>

        <section id="expertise" className="section" aria-labelledby="expertise-heading">
          <h2 id="expertise-heading">Areas of Expertise</h2>
          <div className="expertise-grid">
            {expertise.map((item) => (
              <article className={`expertise-card${item.wide ? ' expertise-card-wide' : ''}`} key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="tag-list">{item.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}</div>
              </article>
            ))}
          </div>
        </section>

        <section id="experience" className="section" aria-labelledby="experience-heading">
          <h2 id="experience-heading">Experience</h2>
          <div className="experience-list">
            {experiences.map((item) => (
              <article className="experience-entry" key={item.organisation}>
                <div className="experience-heading">
                  <div className="experience-identity">
                    <div className={`experience-logo ${item.logoClass}`} aria-hidden="true">
                      <img src={item.logo} alt="" />
                    </div>
                    <div>
                      <h3>{item.organisation}</h3>
                      <p className="role">{item.role}</p>
                    </div>
                  </div>
                  {item.date && <span className="date">{item.date}</span>}
                </div>
                <p>{item.summary}</p>
                <ul>{item.bullets.map((bullet, index) => <li key={index}>{bullet}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>

        <section id="projects" className="section" aria-labelledby="projects-heading">
          <h2 id="projects-heading">Passion Projects</h2>
          <div className="project-list">
            {projects.map((project) => (
              <article className="project-entry" key={project.title}>
                <h3>{project.title}</h3>
                {project.content || <p>{project.description}</p>}
                <div className="tag-list">{project.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}</div>
                {project.links && <div className="project-links">{project.links.map((link) => <a key={link.href} href={link.href} target="_blank" rel="noreferrer">{link.label} →</a>)}</div>}
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <a href="https://www.linkedin.com/in/david-chew-hl/" target="_blank" rel="noreferrer">LinkedIn</a>
        <span aria-hidden="true"> / </span>
        <a href="https://github.com/David-Chew-HL" target="_blank" rel="noreferrer">GitHub</a>
        <span aria-hidden="true"> / </span>
        <a href="https://huggingface.co/David-Chew-HL" target="_blank" rel="noreferrer">Hugging Face</a>
      </footer>
    </div>
  );
}

export default App;
